import {
    ECoinTypeAvailabilityMode, EPaymentMode,
    getDefaultAmount,
    getPairNameFromQuery,
    IAvailablePairDto, IAvailablePairDtoWithModeType,
    ICurrencyDto,
    IValidatedPairsDto,
    parseValidatedPairFormQuery,
    requestGetPairRates,
    TRequestGetPairRatesProps,
} from '../../coins'
import { ECoinInputType } from '../constants'
import { TCoinWidgetState } from '../stateManagement'
import { validateAmount } from './amountLimitValidationHandling'
import { mapPairPriceToWidgetStateState } from './mapPairPriceToWidgetStateState'

type TQueryDto = {
    pair?: string
    from?: string
    to?: string
    amount_base?: string | number
    amount_quote?: string | number
    cur_from?: string
    cur_to?: string
}

const getValidatedQueryPairDto = (
    query: TQueryDto,
    allAvailablePairs: IAvailablePairDto[],
    coinTypeAvailabilityModeFrom: ECoinTypeAvailabilityMode,
): IAvailablePairDtoWithModeType | null => {
    // BestChange custom query params ex: USDCARD should be converted to USD
    // TODO: Mapping object/function
    if (query.cur_from) {
        query.from = query.cur_from.replace("CARD", "")
    }
    if (query.cur_to) {
        query.to = query.cur_to.replace("CARD", "")
    }
    //
    
    const pairName = getPairNameFromQuery({
        pair: query.pair,
        coinCodeFrom: query.from,
        coinCodeTo: query.to,
        coinTypeAvailabilityModeFrom,
    })

    return parseValidatedPairFormQuery(pairName, allAvailablePairs)
}
const getQueriedPairPriceApiReqProps = ({
    pairDto,
    query,
}: {
    pairDto: IAvailablePairDto
    query: { amount_base?: number | string; amount_quote?: number | string }
}): TRequestGetPairRatesProps & { errorMessage: string } => {
    const {
        name: pairName,
        minAmountFrom,
        maxAmountFrom,
        minAmountTo,
        maxAmountTo,
        fromCurrencyDto,
        toCurrencyDto,
    } = pairDto
    const { amount_quote: queriedAmountFrom, amount_base: queriedAmountTo } = query

    const { queriedAmount, queriedAmountInputType } = getQueriedAmountInputType({
        queriedAmountFrom,
        queriedAmountTo,
    })

    if (!queriedAmount) {
        return <TRequestGetPairRatesProps & { errorMessage: string }>{
            pairName,
            amount: getDefaultAmount(minAmountFrom),
            amountOf: ECoinInputType.FROM,
            errorMessage: '',
        }
    } else {
        const { amountQueryCoinCode, minAmount, maxAmount } =
            queriedAmountInputType === ECoinInputType.FROM
                ? {
                      amountQueryCoinCode: fromCurrencyDto.code,
                      minAmount: minAmountFrom,
                      maxAmount: maxAmountFrom,
                  }
                : {
                      amountQueryCoinCode: toCurrencyDto.code,
                      minAmount: minAmountTo,
                      maxAmount: maxAmountTo,
                  }

        const { validatedAmount, errorMessage } = validateAmount({
            coinCode: amountQueryCoinCode,
            amount: queriedAmount,
            minAmount,
            maxAmount,
        })

        return <TRequestGetPairRatesProps & { errorMessage: string }>{
            pairName,
            amount: validatedAmount,
            amountOf: queriedAmountInputType,
            errorMessage,
        }
    }
}
const getQueriedAmountInputType = ({
    queriedAmountFrom,
    queriedAmountTo,
}: {
    queriedAmountFrom?: number | string
    queriedAmountTo?: number | string
}): {
    queriedAmount: number | null
    queriedAmountInputType: ECoinInputType | null
} => {
    if (queriedAmountFrom) {
        return {
            queriedAmount: Number(queriedAmountFrom),
            queriedAmountInputType: ECoinInputType.FROM,
        }
    } else if (queriedAmountTo) {
        return {
            queriedAmount: Number(queriedAmountTo),
            queriedAmountInputType: ECoinInputType.TO,
        }
    }

    return {
        queriedAmount: null,
        queriedAmountInputType: null,
    }
}

const getIsPairQueried = ({ pair, from, to, cur_from, cur_to }: TQueryDto): boolean =>
    Boolean(pair) || (Boolean(from) && Boolean(to)) || (Boolean(cur_from) && Boolean(cur_to))

const getQueriedPairExchangeWidgetState = async ({
    query,
    validatedPairsDto,
    availableCurrencyDtoList,
    coinTypeAvailabilityModeFrom,
}: {
    query: TQueryDto
    validatedPairsDto: IValidatedPairsDto
    availableCurrencyDtoList: ICurrencyDto[]
    coinTypeAvailabilityModeFrom: ECoinTypeAvailabilityMode
}): Promise<{ exchangeWidgetState: TCoinWidgetState; queryError: string; paymentMode?: EPaymentMode }> => {
    if (!getIsPairQueried(query)) {
        return {
            queryError: '',
            exchangeWidgetState: null,
        }
    }

    const queriedPairDto: IAvailablePairDtoWithModeType | null = getValidatedQueryPairDto(
        query,
        validatedPairsDto.allAvailablePairs,
        coinTypeAvailabilityModeFrom,
    )

    const isQueriedPairValid = Boolean(queriedPairDto?.name)
    if (!isQueriedPairValid) {
        return {
            queryError: 'Queried pair is not valid. Pair reset to default one.',
            exchangeWidgetState: null,
        }
    }

    const { errorMessage, ...reqPairPriceProps } = getQueriedPairPriceApiReqProps({
        pairDto: queriedPairDto,
        query,
    })

    const { data: apiPairPrices } = await requestGetPairRates(
        reqPairPriceProps as TRequestGetPairRatesProps,
    )

    return {
        queryError: errorMessage || '',
        exchangeWidgetState: mapPairPriceToWidgetStateState(
            apiPairPrices,
            availableCurrencyDtoList,
        ),
        paymentMode: queriedPairDto.paymentMode,
    }
}

export type { TQueryDto }
export { getQueriedPairExchangeWidgetState }
